import { useMutation, useQuery } from '@tanstack/react-query'

import {
  discardMeetingAttributionGroundTruth,
  finalizeMeetingAttributionGroundTruth,
  getMeetingAttributionGtPrefill,
  type MeetingAttributionGtFinalizeRequest,
  type MeetingAttributionGtSaveRequest,
  saveMeetingAttributionGroundTruth,
} from '@/api/internal'
import { isSeedPending } from '@/pages/app/AttributionViewer/annotate/seedState'

export const attributionGtKeys = {
  all: ['attribution-gt'] as const,
  prefill: (meetingId: string) => [...attributionGtKeys.all, meetingId, 'prefill'] as const,
}

export const SEED_PREPARING_POLL_MS = 5_000

export const READ_DEFAULTS = {
  gcTime: 0,
  staleTime: Infinity,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  // A 504/timeout must not silently re-burn CPU on the server; the page offers an explicit retry.
  retry: false,
} as const

export const useAttributionGtPrefill = (meetingId: string) =>
  useQuery({
    ...READ_DEFAULTS,
    enabled: Boolean(meetingId),
    queryKey: attributionGtKeys.prefill(meetingId),
    queryFn: async ({ signal }) => (await getMeetingAttributionGtPrefill(meetingId, signal)).data,
    refetchInterval: (query) =>
      query.state.status !== 'error' && isSeedPending(query.state.data?.annotatable_reason)
        ? SEED_PREPARING_POLL_MS
        : false,
  })

export const useSaveAttributionGt = (meetingId: string) => {
  return useMutation({
    retry: 0,
    mutationFn: (body: MeetingAttributionGtSaveRequest) => saveMeetingAttributionGroundTruth(meetingId, body),
  })
}

export const useFinalizeAttributionGt = (meetingId: string) => {
  return useMutation({
    retry: 0,
    mutationFn: (body: MeetingAttributionGtFinalizeRequest) => finalizeMeetingAttributionGroundTruth(meetingId, body),
  })
}

export const useDiscardAttributionGt = (meetingId: string) => {
  return useMutation({
    retry: 0,
    mutationFn: (expectedVersion: number) => discardMeetingAttributionGroundTruth(meetingId, expectedVersion),
  })
}
